import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import { MarketingLayout } from "@/components/MarketingLayout";
import client from "@/lib/api";

export default function Blog() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    client.get("/blog").then(({ data }) => setPosts(data)).catch(() => {});
  }, []);

  const [featured, ...rest] = posts;

  return (
    <MarketingLayout>
      <section className="mx-auto max-w-6xl px-6 pt-14 pb-4 md:pt-20">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-ho-primary">Blog</span>
        <h1 className="font-display mt-4 max-w-2xl text-4xl font-black tracking-tighter md:text-6xl">
          Building on your phone, one post at a time
        </h1>
        <p className="mt-5 max-w-lg text-lg text-ho-muted">
          Guides, comparisons, and stories about the phone-first way to build.
        </p>
      </section>

      {featured && (
        <section className="mx-auto max-w-6xl px-6 py-8">
          <Link to={`/blog/${featured.slug}`} data-testid="blog-featured"
            className="tap-scale group grid overflow-hidden rounded-[32px] border border-black/5 bg-white md:grid-cols-2">
            <div className="relative h-56 overflow-hidden md:h-auto">
              <img src={featured.cover} alt={featured.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="flex flex-col justify-center p-8">
              <div className="flex flex-wrap gap-2">
                {featured.tags?.map((t) => (
                  <span key={t} className="rounded-full bg-ho-primary/10 px-3 py-1 text-xs font-semibold text-ho-primary">{t}</span>
                ))}
              </div>
              <h2 className="font-display mt-4 text-2xl font-bold tracking-tight md:text-3xl">{featured.title}</h2>
              <p className="mt-3 text-ho-muted">{featured.excerpt}</p>
              <div className="mt-5 flex items-center gap-4 text-sm text-ho-muted">
                <span>{featured.author}</span>
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {featured.read}</span>
              </div>
            </div>
          </Link>
        </section>
      )}

      <section className="mx-auto max-w-6xl px-6 py-8">
        <div className="grid gap-4 md:grid-cols-2">
          {rest.map((p, i) => (
            <motion.div key={p.slug}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.06, duration: 0.4 }}>
              <Link to={`/blog/${p.slug}`} data-testid={`blog-post-${p.slug}`}
                className="tap-scale group flex gap-4 overflow-hidden rounded-3xl border border-black/5 bg-white p-4">
                <img src={p.cover} alt={p.title} className="h-24 w-24 shrink-0 rounded-2xl object-cover" />
                <div className="flex flex-col justify-center">
                  <h3 className="font-display text-lg font-bold leading-snug">{p.title}</h3>
                  <div className="mt-2 flex items-center gap-3 text-xs text-ho-muted">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {p.read}</span>
                    <span className="inline-flex items-center gap-1 font-semibold text-ho-primary">Read <ArrowRight className="w-3 h-3" /></span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </MarketingLayout>
  );
}
